import { createReducer } from '@reduxjs/toolkit'

import {
  AnalysisGeneration,
  isCancelledGeneration,
  TERMINAL_GENERATION_STATUSES
} from 'types/AnalysisGeneration'

import * as actions from '../../actions/AnalysisGenerations/actions'
import * as authenticationActions from '../../actions/Authentication/actions'

export interface AnalysisGenerationReducerType {
  // Ongoing generations, analysis type id -> generation id. Each is a job
  // in the backend that is polled until it reaches a terminal status.
  generating: Record<string, string>
  // Latest known generation per analysis type.
  latest: Record<string, AnalysisGeneration>
  // Finished while polled and not yet shown, generation id -> generation.
  completed: Record<string, AnalysisGeneration>
  // Analysis types with an open run dialog, which shows the outcome itself.
  followed: Record<string, true>
  fetched: boolean
}

// Both maps are keyed by analysis type id, so a row can find its own
// generation.
const INITIAL_STATE: AnalysisGenerationReducerType = {
  generating: {},
  latest: {},
  completed: {},
  followed: {},
  fetched: false
}

const analysisGenerationReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(actions.getAllSuccess, (state, action) => {
      // The overview is the latest generation per analysis type — restore
      // both the outcomes and the loading state for anything still in flight.
      action.payload.forEach((generation) => {
        state.latest[generation.analysis_type_id] = generation

        if (!TERMINAL_GENERATION_STATUSES.includes(generation.status)) {
          state.generating[generation.analysis_type_id] = generation.id
        }
      })
      state.fetched = true
    })
    .addCase(actions.triggerSuccess, (state, action) => {
      const generation = action.payload

      state.generating[generation.analysis_type_id] = generation.id
      state.latest[generation.analysis_type_id] = generation
    })
    .addCase(actions.cancelSuccess, (state, action) => {
      const generation = action.payload

      delete state.generating[generation.analysis_type_id]
      state.latest[generation.analysis_type_id] = generation
    })
    .addCase(actions.finished, (state, action) => {
      const generation = action.payload

      delete state.generating[generation.analysis_type_id]
      state.latest[generation.analysis_type_id] = generation

      if (
        !isCancelledGeneration(generation) &&
        !state.followed[generation.analysis_type_id]
      ) {
        state.completed[generation.id] = generation
      }
    })
    .addCase(actions.dismissCompleted, (state, action) => {
      delete state.completed[action.payload]
    })
    .addCase(actions.follow, (state, action) => {
      state.followed[action.payload] = true
    })
    .addCase(actions.unfollow, (state, action) => {
      delete state.followed[action.payload]
    })
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
    .addDefaultCase((state) => state)
})

export default analysisGenerationReducer
